/**
 * Draw-distance policy math for the per-tier far plane + linear fog knob
 * (QualityPreset.drawDistanceCeiling in src/config/qualityPresets.config.js).
 * Pure and Babylon/DOM-free so it unit tests in node
 * (tests/utils/drawDistance.test.js), following the src/utils/framePacing.js
 * conventions.
 *
 * The far plane is resolved per level x tier: the tier contributes a CEILING,
 * the level a NEED (how far its geometry actually extends, see
 * levelDrawDistanceNeed). When the ceiling truncates the level, linear fog
 * fades geometry out just inside the clip so the far edge never pops. When it
 * does not, fog is "parked" far beyond all geometry: the scene keeps
 * FOGMODE_LINEAR permanently (levelManager sets it before the level builds so
 * every material - including the frozen ones - compiles the FOG define
 * exactly once) and only the fogStart/fogEnd uniforms ever move, so a tier
 * change never triggers a shader recompile.
 *
 * The skybox is exempt twice over: Babylon 8's createDefaultSkybox sets
 * ignoreCameraMaxZ (the far plane cannot clip it at any size) and
 * EnvironmentManager opts its material out of fog.
 */

/** The historical camera.maxZ - still the fallback when inputs are invalid. */
export const LEGACY_MAX_Z = 2000;

/**
 * Floor for the resolved far plane. Covers the small levels' real extents
 * (Hub ~120, Soccer ~150, BreakoutLounge ~100) with margin, so no tier can
 * ever clip a level a player can actually see across.
 */
export const MIN_DRAW_DISTANCE = 200;

/** Fog begins at this fraction of the far plane when truncating. */
export const FOG_START_RATIO = 0.55;

/** Geometry is fully fogged at this fraction - safely inside the clip edge. */
export const FOG_END_RATIO = 0.92;

/**
 * "Parked" fog band: beyond all geometry, so LINEAR fog contributes nothing
 * while the FOG shader define stays compiled in. Start != end keeps the
 * linear-fog denominator (fogEnd - fogStart) safely nonzero.
 */
export const FOG_PARKED_START = 1e7;
export const FOG_PARKED_END = 2e7;

/** Fallback level need = corner-to-corner map diagonal plus this margin. */
export const MAP_DIAGONAL_MARGIN = 1.05;

export interface DrawDistanceResolution {
  /** Value for camera.maxZ on every renderable camera. */
  maxZ: number;
  /** Whether the tier ceiling actually cut into the level's extent. */
  truncated: boolean;
  /** scene.fogStart - parked beyond all geometry when not truncated. */
  fogStart: number;
  /** scene.fogEnd - always < maxZ when truncated. */
  fogEnd: number;
}

function isPositiveFinite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Resolve the far plane + fog band for one tier ceiling x one level need.
 * Invalid inputs (missing knob, unknown level) fall back to LEGACY_MAX_Z so an
 * unconfigured path renders exactly as before this knob existed.
 */
export function resolveDrawDistance(
  ceiling: number | null | undefined,
  levelNeed: number | null | undefined,
): DrawDistanceResolution {
  const cap = isPositiveFinite(ceiling) ? ceiling : LEGACY_MAX_Z;
  const need = isPositiveFinite(levelNeed) ? levelNeed : LEGACY_MAX_Z;
  const maxZ = Math.max(MIN_DRAW_DISTANCE, Math.min(cap, need));
  const truncated = maxZ < need;
  if (truncated) {
    return {
      maxZ,
      truncated,
      fogStart: FOG_START_RATIO * maxZ,
      fogEnd: FOG_END_RATIO * maxZ,
    };
  }
  return { maxZ, truncated, fogStart: FOG_PARKED_START, fogEnd: FOG_PARKED_END };
}

export interface EnvironmentDescriptor {
  /** How far this level's geometry extends - the uncut far-plane need. */
  drawDistance?: number;
}

/**
 * The slice of a level instance this module reads, duck-typed like every
 * optional level descriptor (getMinimapDescriptor / getZones / ...).
 */
export interface DrawDistanceLevelLike {
  getEnvironmentDescriptor?: () => EnvironmentDescriptor | null | undefined;
  getMapSize?: () => { width: number; height: number } | null | undefined;
}

/**
 * A level's uncut far-plane need:
 * 1. an explicit getEnvironmentDescriptor().drawDistance wins (the exhibition
 *    declares 1150 because its mapSize is a 400x400 gameplay bound, half the
 *    real 800x800 ground);
 * 2. else the getMapSize() corner diagonal plus margin (LayoutLevel's mapSize
 *    mirrors its dynamic groundRect, so arbitrary floor plans auto-size);
 * 3. else the legacy 2000.
 * Never derived from getMinimapDescriptor().bounds - that is a HUD framing
 * rectangle with the wrong extents, and it is null when the minimap is off.
 */
export function levelDrawDistanceNeed(level: DrawDistanceLevelLike | null | undefined): number {
  if (level && typeof level.getEnvironmentDescriptor === 'function') {
    const declared = level.getEnvironmentDescriptor()?.drawDistance;
    if (isPositiveFinite(declared)) return declared;
  }
  if (level && typeof level.getMapSize === 'function') {
    const size = level.getMapSize();
    if (size && isPositiveFinite(size.width) && isPositiveFinite(size.height)) {
      return Math.max(
        MIN_DRAW_DISTANCE,
        Math.ceil(Math.hypot(size.width, size.height) * MAP_DIAGONAL_MARGIN),
      );
    }
  }
  return LEGACY_MAX_Z;
}
